import { useState, useRef, useCallback } from 'react';

import { postAuthorisedJSON } from 'approot/shared/api/auth/auth.http';
import { isResponseBodyOk, isResponseHeadOk } from 'lib/network/http';
import downloadDoc from 'approot/shared/download-doc/download-doc';
import { get } from 'lib/network/http';
import { AnyObject } from 'webclient.constants';
import { InquisitiveLanguageCodes } from 'global/graphql/scalars.type';
import { LANGUAGE_CODE_EN_AU } from 'global/constants';

const useDownloadPages = () => {
  const currentDocData = useRef<string>(undefined);
  const docUrl = useRef<string>(undefined);
  const docFile = useRef<Blob>(undefined);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoading, setIsloading] = useState<boolean>(false);

  const handleDownloadPages = useCallback(
    async ({
      fileName,
      pages,
      assetKey,
      sameAsOriginal,
      headerFields,
      printPdfKeyPublicToken,
      language,
    }: {
      fileName: string;
      pages: number[];
      assetKey: string;
      sameAsOriginal: boolean;
      headerFields?: AnyObject;
      printPdfKeyPublicToken?: string | null;
      language?: InquisitiveLanguageCodes;
    }): Promise<boolean> => {
      const openDoc = downloadDoc.bind(null, {
        fileName,
        docFile,
        docUrl,
        extension: 'pdf',
      });
      const docData = `${assetKey}|${pages.join(',')}`;
      if (isLoading) return false;
      if (currentDocData.current === docData && docUrl.current) {
        openDoc();
        return true;
      }

      setIsloading(true);
      setHasError(false);

      const response = await postAuthorisedJSON({
        path: `/api/download-individual-pages`,
        postJSON: {
          pages,
          assetKey,
          sameAsOriginal,
          printPdfKeyPublicToken,
          language: language === LANGUAGE_CODE_EN_AU ? undefined : language,
        },
        headerFields,
      });

      if (!isResponseBodyOk(response)) {
        setIsloading(false);
        setHasError(true);
        return false;
      }

      if (!(response && response.downloadUrl)) {
        setIsloading(false);
        setHasError(true);
        return false;
      }

      const pdfResponse = await get(
        {
          path: response.downloadUrl,
        },
        'download-pages.tsx'
      );

      if (!isResponseHeadOk(pdfResponse)) {
        setIsloading(false);
        setHasError(true);
      }

      const file =
        pdfResponse && pdfResponse.blob && (await pdfResponse.blob());

      if (!file || !file.size) {
        setIsloading(false);
        setHasError(true);
        return false;
      }

      docFile.current = file;
      docUrl.current = URL.createObjectURL(file);

      setIsloading(false);
      openDoc();
      return true;
    },
    [isLoading]
  );

  return {
    isLoading,
    hasError,
    handleDownloadPages,
  };
};

export default useDownloadPages;
